import { Children } from "react";
import Header from "./Header";
import Footer from "./Footer";
import SideNav from "./SideNav";
import SessionRecoveryNotice from "./SessionRecoveryNotice";
import QuickFacts from "./QuickFacts";
import { useAuth } from "./Auth/useAuth";
import { getPortalLabel } from "../portal/portalUtils.js";

const CONTENT_WIDTH_CLASSES = {
  full: "max-w-none",
  standard: "max-w-[1280px]",
  wide: "max-w-[1340px]",
  map: "max-w-[1580px]",
};

function extractAccentColor(headerBorderColor) {
  const matched = String(headerBorderColor || "").match(/border-\[(#[0-9A-Fa-f]{3,8})\]/);
  return matched?.[1] || "#34a853";
}

export default function Page({
  title,
  children,
  dashboardData = false,
  quickFactsData = null,
  quickFactsVariant = "legacy-strip",
  showQuickFacts = false,
  headerBorderColor = "border-[#34a853]",
  headerActions = null,
  headerVariant = "hybrid",
  introContent = null,
  contentWidth = "full",
  contentSurface = "plain",
  padding = "px-4 py-5 md:px-6 md:py-7",
}) {
  const {
    viewer,
    activePortal,
    hostPortal,
    isPortalSimulated,
    clearPersonaPortalOverride,
    isImpersonating,
  } = useAuth();
  const accentColor = extractAccentColor(headerBorderColor);
  const contentWidthClass =
    CONTENT_WIDTH_CLASSES[contentWidth] || CONTENT_WIDTH_CLASSES.full;
  const isHybridHeader = headerVariant === "hybrid";
  const contentSurfaceClass =
    contentSurface === "card"
      ? "rounded-[8px] border border-slate-200 bg-white shadow-[0_14px_34px_rgba(18,28,45,0.06)]"
      : "";
  const shouldEmbedQuickFactsInHeader =
    showQuickFacts && quickFactsVariant === "inline-card" && isHybridHeader;

  return (
    <div className="flex h-screen w-screen flex-col">
      <Header />

      <div className="flex grow overflow-auto bg-[#fbfbf8]">
        <SideNav />
        <div id="scrollableDiv" className="flex grow flex-col overflow-auto bg-[#fbfbf8]">
          <SessionRecoveryNotice />
          {viewer && !isImpersonating && isPortalSimulated && (
            <div className="bg-[#243245] text-white px-[29px] py-3 flex items-center justify-between gap-4">
              <p className="text-sm md:text-base">
                Portal simulation is active. You are viewing the{" "}
                {getPortalLabel(activePortal)} portal.
              </p>
              <button
                className="border border-white px-3 py-1 text-sm"
                onClick={clearPersonaPortalOverride}
                type="button"
              >
                Return to {getPortalLabel(hostPortal)}
              </button>
            </div>
          )}
          <header
            data-testid="page-header"
            className={`${
              isHybridHeader
                ? "border-b border-slate-200 bg-[#f4f4f1] px-4 py-4 md:px-6 md:py-5"
                : "min-h-[101px] bg-[#eeeeee] px-[29px] py-[19px] text-[34px] font-light text-[#666666]"
            } ${!isHybridHeader ? `border-b-[3px] ${headerBorderColor}` : ""}`}
          >
            {isHybridHeader ? (
              <div className={`mx-auto w-full ${contentWidthClass}`}>
                <div className="relative px-1 py-1 md:px-0">
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <h1 className="break-words text-[26px] font-bold leading-tight text-slate-800 md:text-[32px]">
                        {title}
                      </h1>
                      {introContent ? (
                        <div className="mt-1 max-w-[900px] text-[14px] leading-5 text-slate-600">
                          {introContent}
                        </div>
                      ) : null}
                      {shouldEmbedQuickFactsInHeader ? (
                        <div className="mt-2">
                          <QuickFacts
                            dashboardData={dashboardData}
                            quickFactsData={quickFactsData}
                            sectionColor={accentColor}
                            contentWidth={contentWidth}
                            variant={quickFactsVariant}
                            embedded={true}
                          />
                        </div>
                      ) : null}
                    </div>
                    {headerActions ? (
                      <div className="w-full self-start sm:w-auto sm:shrink-0">{headerActions}</div>
                    ) : null}
                  </div>
                  <div aria-hidden="true" className="pointer-events-none absolute -bottom-5 left-0 h-[3px] w-16 md:-bottom-6" style={{ backgroundColor: accentColor }} />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 break-words">{title}</div>
                {headerActions ? <div className="w-full sm:w-auto sm:shrink-0">{headerActions}</div> : null}
              </div>
            )}
          </header>

          {showQuickFacts && !shouldEmbedQuickFactsInHeader && (
            <QuickFacts
              dashboardData={dashboardData}
              quickFactsData={quickFactsData}
              sectionColor={accentColor}
              contentWidth={contentWidth}
              variant={quickFactsVariant}
              embedded={false}
            />
          )}

          <main className="page-enter flex w-full grow flex-col pb-6">
            <div className={`mx-auto flex w-full grow flex-col ${contentWidthClass}`}>
              <div className={`flex w-full grow flex-col ${padding} ${contentSurfaceClass}`}>
                {Children.map(children, (child) => (
                  <div className="flex w-full">{child}</div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
